import AsyncButton from "@/components/AsyncButton";
import PageHeader from "@/components/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContractorsPlannedHoursWizardResultsPanel } from "@/modules/contractorsPlannedHoursWizard/components/ContractorsPlannedHoursWizardResultsPanel";
import { ContractorsPlannedHoursWizardAllocationsList } from "@/modules/contractorsPlannedHoursWizard/components/lists/ContractorsPlannedHoursWizardAllocationsList";
import { useContractorsPlannedHoursWizardColumnOverrides } from "@/modules/contractorsPlannedHoursWizard/hooks/useContractorsPlannedHoursWizardColumnOverrides";
import {
  type ContractorsPlannedHoursWizardTabId,
  useContractorsPlannedHoursWizardTabs,
} from "@/modules/contractorsPlannedHoursWizard/hooks/useContractorsPlannedHoursWizardTabs";
import { Calculator } from "lucide-react";
import { useTranslation } from "react-i18next";

const TAB_CONTENT_CLASS =
  "m-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden";

export type { ContractorsPlannedHoursWizardTabId };

export function ContractorsPlannedHoursWizardTabs({
  breadcrumbItems,
  columnOverrides,
}: {
  breadcrumbItems: { label: string; href?: string }[];
  columnOverrides: ReturnType<
    typeof useContractorsPlannedHoursWizardColumnOverrides
  >;
}) {
  const { t } = useTranslation();
  const {
    activeTab,
    setActiveTab,
    calculationResults,
    calculationSnapshot,
    calculateMutation,
    handleCalculate,
    calculateDisabled,
    hasUnappliedSelectionChanges,
  } = useContractorsPlannedHoursWizardTabs();
  const handleTabChange = (nextTab: string) =>
    setActiveTab(nextTab as ContractorsPlannedHoursWizardTabId);

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className="flex min-h-0 flex-1 flex-col gap-1"
    >
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        actions={
          <div className="flex items-center gap-2">
            <TabsList className="h-9">
              <TabsTrigger value="selecting">
                {t("contractorsPlannedHoursWizard.tabs.selecting")}
              </TabsTrigger>
              <TabsTrigger value="results">
                {t("contractorsPlannedHoursWizard.tabs.results")}
              </TabsTrigger>
            </TabsList>
            <AsyncButton
              variant="ghost"
              size="sm"
              className="shrink-0"
              label={t("contractorsPlannedHoursWizard.actions.calculate")}
              loadingLabel={t(
                "contractorsPlannedHoursWizard.actions.calculating",
              )}
              loading={calculateMutation.isPending}
              disabled={calculateDisabled}
              onClick={handleCalculate}
              icon={<Calculator className="mr-2 h-4 w-4" />}
            />
          </div>
        }
      />

      <TabsContent value="selecting" className={TAB_CONTENT_CLASS}>
        {hasUnappliedSelectionChanges ? (
          <Alert
            variant="warning"
            className="mb-2 py-2 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2 [&>svg+div]:translate-y-0"
          >
            <AlertDescription className="text-sm leading-5">
              {t("contractorsPlannedHoursWizard.notifications.recalculateHint")}
            </AlertDescription>
          </Alert>
        ) : null}
        <ContractorsPlannedHoursWizardAllocationsList
          columnOverrides={columnOverrides}
        />
      </TabsContent>

      <TabsContent value="results" className={TAB_CONTENT_CLASS}>
        {hasUnappliedSelectionChanges ? (
          <Alert
            variant="warning"
            className="mb-2 py-2 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2 [&>svg+div]:translate-y-0"
          >
            <AlertDescription className="text-sm leading-5">
              {t(
                "contractorsPlannedHoursWizard.notifications.resultsOutdatedHint",
              )}
            </AlertDescription>
          </Alert>
        ) : null}
        <ContractorsPlannedHoursWizardResultsPanel
          results={calculationResults}
          snapshotAllocations={calculationSnapshot}
        />
      </TabsContent>
    </Tabs>
  );
}
